<template>
    <div>
        <b-alert :show="!!errorHandlingMessage" variant="danger" dismissible>{{ errorHandlingMessage }}</b-alert>
        <FormulateForm
            class="tenant-user-form"
            v-model="localTuData"
            name="tenant_user_form"
            @submit="handleSubmit"
            :errors="inputErrors"
            :form-errors="formErrors"
        >
            <div class="form-row mb-3">
                <div class="col">
                    <FormulateInput
                        name="fname"
                        type="text"
                        label="First Name"
                        :placeholder="loading ? '' : 'First name'"
                        validation="required"
                        validation-name="First Name"
                        :disabled="loading"
                        :disable-errors="loading"
                    />
                </div>
                <div class="col">
                    <FormulateInput
                        name="lname"
                        type="text"
                        label="Last Name"
                        :placeholder="loading ? '' : 'Last name'"
                        validation="required"
                        validation-name="Last Name"
                        :disabled="loading"
                        :disable-errors="loading"
                    />
                </div>
            </div>
            <FormulateInput
                name="_email"
                type="email"
                label="Email Address"
                help="Email addresses cannot be changed once a user has been invited."
                :placeholder="loading ? '' : 'person@company.com'"
                validation="required|email"
                validation-name="Email Address"
                :disabled="loading || localTuData.email_is_locked"
                :disable-errors="loading"
            />
            <div class="form-row mb-3">
                <div class="col">
                    <FormulateInput
                        name="job_title"
                        type="text"
                        label="Job Title"
                        :placeholder="loading ? '' : 'Job Title'"
                        :disabled="loading"
                        :disable-errors="loading"
                    />
                </div>
                <div class="col">
                    <FormulateInput
                        name="work_location"
                        type="text"
                        label="Work Location"
                        help="Plant, office, or other location where person is based"
                        :placeholder="loading ? '' : 'Work Location'"
                        :disabled="loading"
                        :disable-errors="loading"
                    />
                </div>
            </div>
            <FormulateInput
                name="is_company_admin"
                type="checkbox"
                label="Company Admin?"
                help="Company Admins can add/remove other users from their company."
                hide-label-icons
            />
            <!-- Oct 2026 - TenantRoles (was is_def_* checkboxes) - only rendered once the options are loaded, -->
            <!-- so if they fail to load, tenant_role_slugs is not sent and the API applies the tenant defaults (or leaves the roles as they are) -->
            <FormulateInput
                v-if="tenantRoleOptionsLoaded && tenantRoleOptions.length"
                name="tenant_role_slugs"
                type="checkbox"
                label="Contact Types:"
                help="This contact should be considered a relevant contact for the following topics:"
                :options="tenantRoleCheckboxOptions"
                input-class="d-flex gap-4"
                hide-label-icons
            />
            <div class="d-flex justify-content-start align-items-center">
                <b-button 
                    variant="primary" 
                    size="lg"
                    :disabled="loading"
                    class="font-size-90"
                    @click="$formulate.submit('tenant_user_form')"
                >
                    <b-spinner small v-if="loading" class="mr-2"></b-spinner>
                    <span v-if="loading" key="loading">Loading</span>
                    <span v-else key="notLoading">{{ isUpdate ? submitTextUpdate : submitTextCreate }}</span>
                </b-button>
                <b-button
                    variant="light-secondary" 
                    size="lg"
                    class="ml-2 font-size-90" 
                    @click="handleCancel"
                >Cancel</b-button>
                <!-- <FormulateInput
                    type="button"
                    class="mr-2"
                    @click="$emit('cancel')"
                >Cancel</FormulateInput>
                <FormulateInput
                    type="submit"
                    :disabled="loading"
                >
                    <b-spinner small v-if="loading" class="mr-2"></b-spinner>
                    <span v-if="loading" key="loading">Loading</span>
                    <span v-else key="notLoading">{{ isUpdate ? submitTextUpdate : submitTextCreate }}</span>
                </FormulateInput> -->
            </div>
        </FormulateForm>
    </div>
</template>

<script>
import { suppliosApi } from '../../js/api-service';
import { errorHandling } from '../../js/custom-vue-mixins';
import merge from 'lodash/merge';

export default {
    name: 'TenantUserForm',
    components: {},
    mixins: [
        errorHandling,
    ],
    props: {
        fixedData: {  // ??? maybe not needed, use initialData instead? pass in any fixed data, we will merge with form data (this wins) and emit it back up merged
            type: Object,
            required: false,
        },
        initialData: {  // if this is an update, pass in the initial data here, we will set it as localTuData
            type: Object,
            required: false,  
        },
        loading: {  // set from parent, the loading indicator
            type: Boolean,
            default: false,
        },
        backendErrors: { // this needs work... pass in a list of field-level and/or form-level errors from backend for display in the correct places on the form
            type: Array,
            required: false,
        },
        hideFields: {  // list of fields to hide
            type: Array,
            required: false,  
        },
        submitTextCreate: {
            type: String,
            default: 'Add User',
        },
        submitTextUpdate: {
            type: String,
            default: 'Update User',
        }
    },
    data: function() {
        return {
            localTuData: {
                is_company_admin: false,  // need this or else vue-formulate may not add it to the object unless toggle on/off
            }, // v-model with the formulate form
            tenantRoleOptions: [],  // [{id, slug, name, description, is_default}] from suppliosApi.contact.tenantRoleOptions()
            tenantRoleOptionsLoaded: false,
            formErrors: [],  // non-field errors for the form, array of strings
            inputErrors: {},  // field-related errors for the form, object
            isUpdate: false,  // maybe you want this to be a prop
        }
    },
    computed: {
        tenantRoleCheckboxOptions() {
            return this.tenantRoleOptions.map(opt => ({ value: opt.slug, label: opt.name }));
        },
    },
    created: function() {
        if (this.initialData) {
            this.localTuData = merge({}, this.localTuData, this.initialData);
        }
        this.loadTenantRoleOptions();
    },
    mounted: function() {
        
    },
    methods: {
        loadTenantRoleOptions: async function() {
            // seed tenant_role_slugs from initialData.tenant_roles (update), otherwise preselect the roles new contacts get by default
            try {
                const { data } = await suppliosApi.contact.tenantRoleOptions();
                this.tenantRoleOptions = data;
                this.tenantRoleOptionsLoaded = true;
                const heldRoles = this.initialData?.tenant_roles;
                const slugs = heldRoles ? heldRoles.map(role => role.slug) : data.filter(opt => opt.is_default).map(opt => opt.slug);
                this.localTuData = { ...this.localTuData, tenant_role_slugs: slugs };
            } catch (error) {
                this.apiErrorHandler(error, 'Error while getting contact role options', true);
            }
        },
        // use this.apiErrorHandler(error, extraMsg, localMsg) from mixin - localMsg is bool, if true puts message in this.errorHandlingMessage
        handleSubmit: function(submitData) {
            // handle the actual form submission (from Formulate Form @submit handler, not from the button)
            // only gets triggered when everything is valid 
            // emit the full TU data object as payload with 'submit' event
            this.$emit('submit', submitData);
        },
        handleCancel: function() {
            // just emit 'cancel' event 
            this.$emit('cancel');
        }
    }
}
</script>

<style>

</style>
